import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  signal,
  viewChild,
} from '@angular/core';
import { FINDER_PRODUCTS, FINDER_QUESTIONS, FinderProduct, FinderQuestionId } from '../content';
import { Popups, storage } from '../popups';

/** localStorage: the finder was completed here — it no longer opens by itself */
const DONE_KEY = 'lnRfCompleted';
/** sessionStorage: the step and the answers, so that reopening it carries on rather than starts again */
const STATE_KEY = 'lnRfState';
/** it opens by itself this long after the page loads, or once this much of the page has been scrolled */
const AUTO_DELAY_MS = 6000;
const AUTO_SCROLL = 0.35;

type Answers = Partial<Record<FinderQuestionId, string>>;

/** The sofa outline beside a "how many people" option, and the seams that split it into 2 or 3 seats. */
const SOFA = 'M4 12V8a3 3 0 0 1 3-3h22a3 3 0 0 1 3 3v4';
const SEAMS = { 1: '', 2: 'M18 12v7', 3: 'M13 12v7M23 12v7' };

/** Up to four products for these answers, best match first — and never fewer than three. */
function match(answers: Answers): FinderProduct[] {
  const scored = FINDER_PRODUCTS.map((product) => {
    let score = 0;
    for (const question of FINDER_QUESTIONS) {
      const answer = answers[question.id];
      // skipped, or "not sure": rules nothing out
      if (!answer || answer === 'unsure') continue;
      if (answer === 'any' || product.suits[question.id].includes(answer)) score += question.weight;
    }
    return { product, score };
  });
  // (the sort is stable: equal scores keep the catalogue's order)
  scored.sort((a, b) => b.score - a.score);
  const top = scored.filter((entry) => entry.score > 0).slice(0, 4);
  for (const entry of scored) {
    if (top.length >= 3) break;
    if (!top.includes(entry)) top.push(entry);
  }
  return top.map((entry) => entry.product);
}

/** The label of the answer given to a question, if it has one. */
function chosen(answers: Answers, id: FinderQuestionId): string | undefined {
  return FINDER_QUESTIONS.find((q) => q.id === id)?.options.find((o) => o.value === answers[id])?.label;
}

/** One line on why these products were picked, built from the answers. */
function reason(answers: Answers): string {
  const label = (id: FinderQuestionId) => chosen(answers, id)?.toLowerCase();
  const parts = [
    label('seats'),
    answers.fabric !== 'any' && label('fabric'),
    label('feature'),
    answers.place && `for a ${label('place')}`,
    answers.budget && answers.budget !== 'unsure' && `around ${label('budget')}`,
  ].filter(Boolean);
  return parts.length ? `Picked for you: ${parts.join(', ')}.` : 'Our most popular recliners to start you off.';
}

/**
 * The Recliner Finder popup: an intro, five questions (one per step, each can
 * be skipped) and a short list of suggestions. Everything happens in the
 * browser — the questions and the products come from content.ts.
 */
@Component({
  selector: 'app-finder',
  templateUrl: './finder.html',
  styleUrl: './finder.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Finder {
  protected readonly popups = inject(Popups);
  protected readonly total = FINDER_QUESTIONS.length;
  protected readonly sofa = SOFA;
  protected readonly seams = SEAMS;

  protected readonly open = computed(() => this.popups.open() === 'finder');
  /** 0 is the intro, 1 to `total` the questions, anything above the results */
  protected readonly step = signal(0);
  protected readonly answers = signal<Answers>({});

  /** the question of this step — none on the intro and on the results */
  protected readonly question = computed(() => (this.step() >= 1 ? FINDER_QUESTIONS[this.step() - 1] : undefined));
  protected readonly head = computed(() => {
    const question = this.question();
    if (question) return { eyebrow: `Question ${this.step()}`, title: question.title };
    if (this.step() === 0) return { eyebrow: 'Recliner Finder', title: 'Not sure which recliner is right?' };
    return { eyebrow: 'Your matches', title: 'Recliners that suit you' };
  });
  /** how far the bar is filled, in percent, and the "n of 5" beside it */
  protected readonly progress = computed(() => Math.round((Math.min(this.step(), this.total) / this.total) * 100));
  protected readonly count = computed(() => Math.min(Math.max(this.step(), 1), this.total));
  protected readonly picks = computed(() => (this.step() > this.total ? match(this.answers()) : []));
  protected readonly reason = computed(() => reason(this.answers()));

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly body = viewChild.required<ElementRef<HTMLElement>>('body');
  private readonly injector = inject(Injector);

  constructor() {
    afterNextRender(() => {
      this.restore();
      if (!storage.get('local', DONE_KEY)) this.popups.autoOpen('finder', AUTO_DELAY_MS, AUTO_SCROLL);
    });
  }

  protected go(step: number): void {
    this.step.set(step);
    this.save();
    if (step > this.total) storage.set('local', DONE_KEY, String(Date.now()));

    // the old step's controls are gone: carry on from the new step's choices, or its main button
    afterNextRender(
      () => {
        const body = this.body().nativeElement;
        body.scrollTop = 0;
        const next =
          body.querySelector<HTMLElement>('input:checked') ??
          body.querySelector<HTMLElement>('input') ??
          this.host.querySelector<HTMLElement>('.finder__btn--primary');
        next?.focus();
      },
      { injector: this.injector },
    );
  }

  protected choose(id: FinderQuestionId, value: string): void {
    this.answers.update((answers) => ({ ...answers, [id]: value }));
    this.save();
  }

  /** Moves on without an answer: the question then rules nothing out. */
  protected skip(id: FinderQuestionId): void {
    this.answers.update((answers) => {
      const rest = { ...answers };
      delete rest[id];
      return rest;
    });
    this.go(this.step() + 1);
  }

  protected restart(): void {
    this.answers.set({});
    this.go(0);
  }

  /** Hands over to the enquiry popup, with the answers ready in its Message field. */
  protected expert(): void {
    const answers = this.answers();
    const lines = FINDER_QUESTIONS.filter((q) => chosen(answers, q.id)).map((q) => `${q.title} ${chosen(answers, q.id)}`);
    this.popups.note.set(lines.length ? `From the Recliner Finder:\n${lines.join('\n')}` : '');
    this.popups.show('enquiry');
  }

  private save(): void {
    storage.set('session', STATE_KEY, JSON.stringify({ step: this.step(), answers: this.answers() }));
  }

  private restore(): void {
    try {
      const saved = JSON.parse(storage.get('session', STATE_KEY) ?? 'null');
      if (typeof saved?.step !== 'number' || typeof saved.answers !== 'object') return;
      this.step.set(Math.min(Math.max(Math.trunc(saved.step), 0), this.total + 1));
      this.answers.set(saved.answers ?? {});
    } catch {
      // nothing usable was saved: it starts from the intro
    }
  }
}
